import { useRef, useState, useEffect } from "react";
import { Lock, Check, ArrowLeft } from "react-feather";
import { useAuth, useMessage } from "../../../global-components/src";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { formatCurrency } from "../../../utils/numbers";
import { errorMessage } from "../../../global-components/src/utils/text";
import { User } from "../../../global-components/src/types";
import { useTranslation } from 'react-i18next';
import { usePasswordCache } from '../../../hooks/usePasswordCache';

interface GiveCoffeeContentProps {
    user?: User | null;
    onClose?: () => void;
}

type Step = "amount" | "confirm" | "success";

const QUICK_AMOUNTS = [5, 10, 20, 50];
const LIMIT = 100;

const GiveCoffeeContent: React.FC<GiveCoffeeContentProps> = ({ user, onClose }) => {
    const [step, setStep] = useState<Step>("amount");
    const [text, setText] = useState("");
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    const { token } = useAuth();
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const [errorsInput, setErrorsInput] = useState<any>({});
    const [value, setValue] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [hasCachedPassword, setHasCachedPassword] = useState(false);
    const { savePassword, getPassword } = usePasswordCache();

    useEffect(() => {
        const cached = getPassword();
        setHasCachedPassword(!!cached);
        if (cached) setPassword(cached);
    }, [getPassword]);

    const parsedValue = () => {
        let valueFormmater = value;
        valueFormmater = valueFormmater.replace(/R\$\s*/g, "");
        valueFormmater = valueFormmater.replace(/\s+/g, "");
        valueFormmater = valueFormmater.replace(/\./g, "");
        valueFormmater = valueFormmater.replace(/,/g, ".");
        return Number(valueFormmater);
    };

    const limitePost = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = event.target.value;
        setText(newText);
        setIsLimitExceeded(newText.length > LIMIT);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(formatCurrency(event.target.value));
    };

    const handleQuickAmount = (amount: number) => {
        setValue(amount.toFixed(2).replace(".", ","));
        setErrorsInput({});
    };

    const goToConfirm = () => {
        const valueNumber = parsedValue();
        const errors: Record<string, string[]> = {};

        if (!valueNumber) errors.amount = [t("errors.input.required", { field: t("generics.value") })];
        else if (valueNumber < 1) errors.amount = [t("coffee.minValue")];
        if (text.length > LIMIT) errors.message = [t("errors.input.maxLength", { field: t("generics.message"), max: 100 })];

        if (Object.keys(errors).length > 0) {
            setErrorsInput(errors);
            return;
        }
        setErrorsInput({});
        setStep("confirm");
    };

    const handleReport = async () => {
        setError("");
        setIsLoading(true);
        const url = PACOCA_API_URL + `/wallet/transfer`;

        try {
            const valueNumber = parsedValue();
            const errors: Record<string, string[]> = {};

            if (!password.trim().length) errors.password = [t("errors.input.required", { field: t("inputs.password") })];

            if (Object.keys(errors).length > 0) {
                throw { response: { data: { errors } } };
            }

            await axios.post(
                url,
                { id_user: user?.id, message: text, password, amount: valueNumber },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            savePassword(password);
            setStep("success");
            setMessage(t("coffee.tokensSent"), "success");

            setTimeout(() => {
                if (closeRef.current) closeRef.current.click();
                setText("");
                setPassword("");
                setValue("");
                setErrorsInput({});
                setStep("amount");
                if (onClose) onClose();
            }, 1800);
        } catch (err: any) {
            const messageError = errorMessage(err);
            console.error(err);

            if (err?.response?.data.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                setError(messageError);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const initials = (user?.user_name || "?").slice(0, 1).toUpperCase();

    if (step === "success") {
        return (
            <div className="bank-tx bank-tx-success">
                <div className="bank-check-circle">
                    <Check size={28} strokeWidth={3} />
                </div>
                <h4>Café enviado!</h4>
                <p className="bank-tx-subtitle">R$ {value || "0,00"} para @{user?.user_name}</p>
            </div>
        );
    }

    return (
        <div className="foc-body">
            <div className="bank-steps">
                <span className={`bank-step-dot ${step === "amount" ? "active" : "done"}`} />
                <span className={`bank-step-dot ${step === "confirm" ? "active" : ""}`} />
            </div>

            {step === "amount" && (
                <>
                    {/* <div className="bank-recipient">
                        <div className="bank-avatar">{initials}</div>
                        <div>
                            <span className="bank-recipient-label">Enviando café para</span>
                            <strong className="bank-recipient-name">@{user?.user_name}</strong>
                        </div>
                    </div> */}

                    <div className="bank-amount-block">
                        <input
                            type="text"
                            value={value}
                            onChange={handleChange}
                            placeholder="0,00"
                            className="bank-amount-input"
                            autoFocus
                        />
                    </div>
                    {errorsInput.amount && (
                        <div role="alert" className="bank-error">{errorsInput.amount[0]}</div>
                    )}

                    <div className="bank-quick-row">
                        {QUICK_AMOUNTS.map((amount) => (
                            <button
                                type="button"
                                key={amount}
                                className="bank-chip"
                                onClick={() => handleQuickAmount(amount)}
                            >
                                R$ {amount}
                            </button>
                        ))}
                    </div>

                    <div className="bank-field">
                        <label>Mensagem (opcional)</label>
                        <textarea
                            value={text}
                            onChange={limitePost}
                            placeholder={t("coffee.placeholder")}
                            className={`bank-textarea ${errorsInput.message ? "is-invalid" : ""}`}
                        />
                        {errorsInput.message && (
                            <div role="alert" className="bank-error">{errorsInput.message[0]}</div>
                        )}
                    </div>

                    <div className="foc-actions">
                        <button
                            type="button"
                            className="foc-btn foc-btn-primary"
                            disabled={isLimitExceeded}
                            onClick={goToConfirm}
                        >
                            Continuar
                        </button>
                    </div>

                </>
            )}

            {step === "confirm" && (
                <>
                    <button type="button" className="bank-back" onClick={() => setStep("amount")}>
                        <ArrowLeft size={18} /> Voltar
                    </button>

                    <div className="bank-summary-card">
                        <span className="bank-summary-label">Você está enviando</span>
                        <span className="bank-summary-amount">R$ {value || "0,00"}</span>
                        <div className="bank-summary-row">
                            <span>Para</span>
                            <strong>@{user?.user_name}</strong>
                        </div>
                        {text && (
                            <div className="bank-summary-row">
                                <span>Mensagem</span>
                                <strong>{text}</strong>
                            </div>
                        )}
                    </div>

                    {!hasCachedPassword && (
                        <div className="bank-field">
                            <label><Lock size={14} /> Confirme com sua senha</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Senha"
                                className={`bank-input ${errorsInput.password ? "is-invalid" : ""}`}
                            />
                            {errorsInput.password && (
                                <div role="alert" className="bank-error">{errorsInput.password[0]}</div>
                            )}
                        </div>
                    )}

                    {error && <div role="alert" className="bank-error bank-error-general">{error}</div>}

                    <button
                        type="button"
                        className="bank-btn-primary"
                        disabled={isLoading}
                        onClick={handleReport}
                    >
                        {!isLoading ? t("confirmationModal.confirmSend") : <Spinner />}
                    </button>
                </>
            )}
        </div>
    );
};

export default GiveCoffeeContent;